import React, { useState, useEffect, useRef } from 'react';
import { platformImage, slotsData } from '../data/mockData';

const CATALOG_GAMES = Object.values(slotsData).reduce((n, list) => n + list.length, 0);
const CATALOG_PROVIDERS = Object.values(slotsData).filter((list) => list.length > 0).length;
import OrbitingLogos from './OrbitingLogos';
import MarioPlayScene from './MarioPlayScene';
import MarioBackground from './MarioBackground';
import MarioBlockEntrar from './MarioBlockEntrar';
import GoombaPatrol from './GoombaPatrol';
import KonamiEasterEgg from './KonamiEasterEgg';

// Data
const gameProviders = [
  { name: 'PG', fullName: 'PG Soft', color: '#F59E0B', logo: '/providers/pg-soft.png' },
  { name: 'PP', fullName: 'Pragmatic Play', color: '#3B82F6', logo: '/providers/pragmatic-play.png' },
  { name: 'Spirit', fullName: 'Spirit Gaming', color: '#8B5CF6', logo: '/providers/spirit.png' },
  { name: 'Tada', fullName: 'Tada Gaming', color: '#EC4899', logo: '/providers/tada.png' },
  { name: 'Reeveme', fullName: 'Reeveme', color: '#10B981', logo: '/providers/reeveme.png' },
  { name: 'FC', fullName: 'FA CHAI', color: '#3B5998', logo: '/providers/fachai.png' },
  { name: 'JDB', fullName: 'JDB', color: '#F97316', logo: '/providers/jdb.png' },
  { name: 'Micro', fullName: 'Micro Gaming', color: '#22C55E', logo: '/providers/microgaming.png' },
  { name: '3 OAKS', fullName: '3 OAKS', color: '#F59E0B', logo: '/providers/threeoaks.png' },
  { name: 'Amigo', fullName: 'Amigo Gaming', color: '#EC4899', logo: '/providers/amigo.png' }
];

const platformLogos = [
  { name: 'SGJOGO', logo: '/assets/platforms/sgjogo.png' },
  { name: 'XGJOGO', logo: '/assets/platforms/xgjogo.png' },
  { name: 'VGJOGO', logo: '/assets/platforms/vgjogo.png' },
  { name: 'HGJOGO', logo: '/assets/platforms/hgjogo.png' },
  { name: 'EGJOGO', logo: '/assets/platforms/egjogo.png' },
  { name: 'FGJOGO', logo: '/assets/platforms/fgjogo.png' },
  { name: 'MGJOGO', logo: '/assets/platforms/mgjogo.png' },
  { name: 'WGJOGO', logo: '/assets/platforms/wgjogo.png' },
  { name: 'AGJOGO', logo: '/assets/platforms/agjogo.png' },
  { name: 'BGJOGO', logo: '/assets/platforms/bgjogo.png' },
  { name: 'YGJOGO', logo: '/assets/platforms/ygjogo.png' },
  { name: 'DGJOGO', logo: '/assets/platforms/dgjogo.png' }
];

// HG JOGO Platform Link (para usar no futuro se necessário)
const HGJOGO_REGISTER_URL = 'https://hgjogo7.com/en/?ch=110006&ic=140396339#/register';

const responsibilityBadges = [
  { name: 'Responsible Gambling', icon: 'RG' },
  { name: 'GamCare', icon: 'GC' },
  { name: 'BetBlocker', icon: 'BB' },
  { name: '18+', icon: '18+' }
];

// Badge "POPULARES" (Mario-style pill) reutilizável
const PopularBadge = ({ testId, gradient = 'linear-gradient(180deg, #fbbf24 0%, #f59e0b 55%, #b45309 100%)', glow = 'rgba(251,191,36,0.85)' }) => (
  <div
    className="absolute -top-2.5 left-3 z-20 pointer-events-none animate-recommended-bob"
    data-testid={testId}
  >
    <div
      className="flex items-center gap-1 px-2 py-0.5 rounded-full"
      style={{
        background: gradient,
        border: '2px solid #000',
        boxShadow: `0 0 12px ${glow}, 2px 2px 0 #000, inset 0 1px 0 rgba(255,255,255,0.6)`,
      }}
    >
      <span className="text-white text-[9px] leading-none" style={{ textShadow: '0 0 2px rgba(0,0,0,0.7)' }}>★</span>
      <span
        className="text-white font-black text-[8px] leading-none tracking-wider"
        style={{ fontFamily: '"Press Start 2P", monospace', paddingTop: '1px', textShadow: '1px 1px 0 #000' }}
      >
        POPULARES
      </span>
    </div>
  </div>
);


const PlatformSelector = ({ onPlatformSelect }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [glitchActive, setGlitchActive] = useState(false);
  
  const footerRef = useRef(null);
  const coreRef = useRef(null);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
    setTimeout(() => setHeroVisible(true), 300);
    setTimeout(() => setCardsVisible(true), 700);
    setTimeout(() => setCtaVisible(true), 1000);
    
    // Glitch spark effect every 8-12 seconds
    const triggerGlitch = () => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 80);
      const nextGlitch = 8000 + Math.random() * 4000;
      setTimeout(triggerGlitch, nextGlitch);
    };
    const glitchTimeout = setTimeout(triggerGlitch, 8000 + Math.random() * 4000);
    
    // Mouse parallax handler for desktop
    const handleMouseMove = (e) => {
      if (!coreRef.current) return;
      const rect = coreRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const x = ((e.clientX - centerX) / (window.innerWidth / 2)) * 6;
      const y = ((e.clientY - centerY) / (window.innerHeight / 2)) * 6;
      setMousePosition({ x: Math.max(-6, Math.min(6, x)), y: Math.max(-6, Math.min(6, y)) });
    };
    
    window.addEventListener('mousemove', handleMouseMove);

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setFooterVisible(true);
    }, { threshold: 0.2 });
    if (footerRef.current) observer.observe(footerRef.current);

    return () => { 
      clearTimeout(glitchTimeout);
      window.removeEventListener('mousemove', handleMouseMove);
      observer.disconnect(); 
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#06060c] text-white overflow-x-hidden">
      
      {/* ════════════════════════════════════════════════════════════════════════
          PREMIUM ANIMATED BACKGROUND
      ════════════════════════════════════════════════════════════════════════ */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0814] via-[#06060c] to-[#080612] animate-bg-breathing" />
        <div className="absolute top-[-200px] right-[-200px] w-[700px] h-[700px] rounded-full bg-violet-600/4 blur-[150px] animate-glow-pulse" />
        <div className="absolute bottom-[-200px] left-[-200px] w-[500px] h-[500px] rounded-full bg-blue-600/3 blur-[120px] animate-glow-pulse-delay" />
        
        {/* Floating particles */}
        {[...Array(25)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full animate-float-particle"
            style={{
              width: `${2 + Math.random() * 2}px`,
              height: `${2 + Math.random() * 2}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: ['#7c3aed', '#3b82f6', '#6366f1', '#f59e0b'][i % 4],
              opacity: 0.3,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${10 + Math.random() * 12}s`
            }}
          />
        ))}
        
        {/* Star sparkles - Mario reference */}
        {[...Array(12)].map((_, i) => (
          <div
            key={`sparkle-${i}`}
            className="absolute animate-star-sparkle"
            style={{
              left: `${40 + Math.random() * 50}%`,
              top: `${10 + Math.random() * 60}%`,
              animationDelay: `${Math.random() * 5}s`
            }}
          >
            <svg className="w-2 h-2 text-amber-400/40" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" />
            </svg>
          </div>
        ))}
        
        {/* Light beams */}
        <div className="absolute top-0 left-[20%] w-[1px] h-full bg-gradient-to-b from-transparent via-violet-500/8 to-transparent animate-beam-pass" />
        <div className="absolute top-0 right-[30%] w-[1px] h-full bg-gradient-to-b from-transparent via-blue-500/6 to-transparent animate-beam-pass-delay" />
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          MARIO-THEMED BACKGROUND (pixel clouds, hills, coins, bushes, pipes, brick)
      ════════════════════════════════════════════════════════════════════════ */}
      <MarioBackground />

      {/* Floating Goombas patrolling the ground + Konami Code easter egg */}
      <GoombaPatrol />
      <KonamiEasterEgg />

      <div className="relative z-10">

        {/* ════════════════════════════════════════════════════════════════════════
            HEADER - Clean minimal
        ════════════════════════════════════════════════════════════════════════ */}
        <header className={`py-4 px-4 sm:px-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Mario-style logo/title on the left */}
            <div className="flex items-center gap-2">
              <img
                src="/assets/mario/mario-pixel-clean.png"
                alt=""
                className="w-7 h-7"
                style={{ imageRendering: 'pixelated', filter: 'drop-shadow(0 0 6px rgba(239,68,68,0.8))' }}
              />
              <span
                className="text-white font-black text-[11px] sm:text-[12px] tracking-wider"
                style={{
                  fontFamily: '"Press Start 2P", monospace',
                  textShadow: '2px 2px 0 #ef4444, 3px 3px 0 #7c2d12',
                }}
              >
                MARIO<span className="text-yellow-300"> AI</span>
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md"
              style={{
                background: 'linear-gradient(180deg, #16a34a 0%, #15803d 100%)',
                border: '2px solid #000',
                boxShadow: '2px 2px 0 #000, inset 0 1px 0 rgba(255,255,255,0.4)',
              }}
            >
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" style={{ boxShadow: '0 0 6px #ef4444' }} />
              <span
                className="text-white font-black text-[8px] tracking-wider"
                style={{ fontFamily: '"Press Start 2P", monospace', textShadow: '1px 1px 0 #000', paddingTop: '1px' }}
              >
                ONLINE
              </span>
            </div>
          </div>
        </header>

        {/* ════════════════════════════════════════════════════════════════════════
            HERO SECTION - Centered Animated Icon Only
        ════════════════════════════════════════════════════════════════════════ */}
        <main className="px-4 sm:px-6 pt-6 pb-28 sm:py-8">
          <div className="max-w-7xl mx-auto">
            
            {/* Hero - Centered Icon */}
            <div className="flex flex-col items-center justify-center min-h-[55vh] sm:min-h-[60vh] mb-8 sm:mb-10">
              
              {/* ════════ CENTERED ARCANE CORE ════════ */}
              <div 
                ref={coreRef}
                className={`relative w-[200px] h-[200px] sm:w-[260px] sm:h-[260px] lg:w-[300px] lg:h-[300px] transition-all duration-1000 ${heroVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}
                style={{
                  transform: `perspective(1000px) rotateX(${-mousePosition.y * 0.5}deg) rotateY(${mousePosition.x * 0.5}deg)`,
                  transition: 'transform 0.15s ease-out'
                }}
              >
                
                {/* ═══════ PROJECTED GLOW ON BACKGROUND (Aura) ═══════ */}
                <div 
                  className="absolute inset-[-40px] sm:inset-[-60px] lg:inset-[-80px] rounded-full pointer-events-none"
                  style={{
                    background: 'radial-gradient(circle, rgba(124, 58, 237, 0.12) 0%, rgba(59, 130, 246, 0.08) 30%, transparent 60%)',
                    filter: 'blur(50px)'
                  }}
                />
                
                {/* ═══════ ARCANE CORE - HOLOGRAPHIC BASE ═══════ */}
                <div className="absolute inset-0 flex items-center justify-center">
                  
                  {/* Outer glow */}
                  <div 
                    className="absolute w-[260px] h-[260px] sm:w-[320px] sm:h-[320px] lg:w-[380px] lg:h-[380px] rounded-full animate-core-glow-premium"
                    style={{
                      background: 'radial-gradient(circle, rgba(124, 58, 237, 0.12) 0%, rgba(59, 130, 246, 0.08) 40%, transparent 70%)',
                      filter: 'blur(40px)'
                    }}
                  />
                  
                  {/* ═══════ RADAR RINGS (Expanding/Fading) ═══════ */}
                  <div 
                    className="absolute w-[220px] h-[220px] sm:w-[280px] sm:h-[280px] lg:w-[340px] lg:h-[340px] rounded-full animate-radar-ring-1"
                    style={{
                      border: '1.5px solid rgba(124, 58, 237, 0.3)',
                      boxShadow: '0 0 30px rgba(124, 58, 237, 0.2), inset 0 0 30px rgba(59, 130, 246, 0.08)'
                    }}
                  />
                  
                  <div 
                    className="absolute w-[180px] h-[180px] sm:w-[230px] sm:h-[230px] lg:w-[280px] lg:h-[280px] rounded-full animate-radar-ring-2"
                    style={{
                      border: '1px solid rgba(59, 130, 246, 0.25)',
                      boxShadow: '0 0 25px rgba(59, 130, 246, 0.15)'
                    }}
                  />
                  
                  <div 
                    className="absolute w-[140px] h-[140px] sm:w-[180px] sm:h-[180px] lg:w-[220px] lg:h-[220px] rounded-full animate-radar-ring-3"
                    style={{
                      border: '1px solid rgba(147, 197, 253, 0.2)',
                      boxShadow: '0 0 20px rgba(124, 58, 237, 0.18), inset 0 0 20px rgba(59, 130, 246, 0.1)'
                    }}
                  />
                  
                  {/* Center nucleus */}
                  <div 
                    className="absolute w-[110px] h-[110px] sm:w-[140px] sm:h-[140px] lg:w-[170px] lg:h-[170px] rounded-full animate-nucleus-breathe"
                    style={{
                      background: 'radial-gradient(circle, rgba(124, 58, 237, 0.2) 0%, rgba(59, 130, 246, 0.12) 50%, transparent 70%)',
                      boxShadow: '0 0 50px rgba(124, 58, 237, 0.35), inset 0 0 30px rgba(59, 130, 246, 0.2)'
                    }}
                  />

                  {/* ═══════ MARIO PLAY SCENE (NES-style cycle: walk→jump→hit block→coin) ═══════ */}
                  <MarioPlayScene glitchActive={glitchActive} />
                  
                  {/* ═══════ HOLOGRAPHIC FLOOR RING WITH MARIO SHADOW ═══════ */}
                  <div 
                    className="absolute bottom-[12%] sm:bottom-[10%] lg:bottom-[8%] w-[150px] h-[35px] sm:w-[180px] sm:h-[40px] lg:w-[220px] lg:h-[50px]"
                    style={{
                      background: 'radial-gradient(ellipse at center, rgba(124, 58, 237, 0.2) 0%, rgba(59, 130, 246, 0.1) 40%, transparent 70%)',
                      borderRadius: '50%',
                      filter: 'blur(6px)'
                    }}
                  />
                  {/* Mario projected shadow */}
                  <div 
                    className="absolute bottom-[10%] sm:bottom-[8%] lg:bottom-[6%] w-[50px] h-[12px] sm:w-[60px] sm:h-[15px] lg:w-[75px] lg:h-[18px] animate-shadow-breathe"
                    style={{
                      background: 'radial-gradient(ellipse at center, rgba(0, 0, 0, 0.4) 0%, rgba(124, 58, 237, 0.25) 50%, transparent 70%)',
                      borderRadius: '50%',
                      filter: 'blur(3px)'
                    }}
                  />
                  
                  {/* ═══════ ORBITING PLATFORM LOGOS ═══════ */}
                  <OrbitingLogos mousePosition={mousePosition} glitchActive={glitchActive} />
                </div>
              </div>
              
              {/* Subtitle below icon */}
              <p className={`mt-32 sm:mt-40 lg:mt-44 text-gray-500 text-xs sm:text-sm text-center transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                Análise profissional de padrões
              </p>
            </div>

            {/* ════════════════════════════════════════════════════════════════════════
                LIVE SIGNALS SECTION - Platform de Sinais ao Vivo (SIMPLIFICADO)
            ════════════════════════════════════════════════════════════════════════ */}
            <div className={`space-y-4 transition-all duration-700 ${cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              
              {/* ════════ LIVE SIGNALS BADGE - Mario style ════════ */}
              <div className="flex items-center justify-center mb-4">
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-md animate-live-pulse"
                  style={{
                    background: 'linear-gradient(180deg, #16a34a 0%, #15803d 100%)',
                    border: '3px solid #000',
                    boxShadow: '0 0 14px rgba(16, 185, 129, 0.55), 3px 3px 0 #000, inset 0 1px 0 rgba(255,255,255,0.4)',
                  }}
                >
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" style={{ boxShadow: '0 0 6px #ef4444' }} />
                  <span
                    className="text-white font-black text-[9px] tracking-widest"
                    style={{
                      fontFamily: '"Press Start 2P", monospace',
                      textShadow: '2px 2px 0 #000',
                      paddingTop: '1px',
                    }}
                  >
                    SINAIS AO VIVO
                  </span>
                </div>
              </div>
              
              {/* ════════ SINAIS AO VIVO + PROVIDERS in same row (desktop) ════════ */}
              <div className="flex flex-col lg:flex-row gap-3 justify-center items-stretch">
                
                {/* SINAIS AO VIVO - COMPACT PREMIUM CARD */}
                <div className="w-full lg:w-auto lg:min-w-[200px]">
                  <div className="group relative h-full min-h-[100px] rounded-xl overflow-hidden transition-all duration-300 hover:translate-y-[-3px]">
                    {/* Animated border glow */}
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-violet-500/25 via-blue-500/20 to-violet-500/25 opacity-60 group-hover:opacity-90 transition-opacity animate-border-glow" />
                    
                    <div className="relative h-full m-[1px] rounded-xl bg-[#0c0c14]/95 backdrop-blur-xl p-4 border border-violet-400/10 group-hover:border-violet-400/25 transition-all flex flex-col justify-center">
                      <div className="flex items-center gap-3">
                        {/* Larger icon with glow */}
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-blue-500/15 flex items-center justify-center border border-violet-400/20 relative">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-400 to-blue-400 animate-pulse-subtle" />
                          <div className="absolute inset-0 rounded-xl bg-violet-500/10 blur-md animate-pulse" />
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-white">SINAIS AO VIVO</h3>
                          <p className="text-gray-500 text-[11px]">Entradas recomendadas</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* PROVIDERS - Fixed centered grid */}
                <div className="flex-1 max-w-3xl">
                  {/* Desktop: 8 columns */}
                  <div className="hidden lg:grid grid-cols-8 gap-3">
                    {gameProviders.map((provider) => (
                      <div key={provider.name} className="group relative min-h-[100px] transition-all duration-300 hover:translate-y-[-3px]">
                        <div 
                          className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-50 transition-all duration-300"
                          style={{ 
                            background: `radial-gradient(circle, ${provider.color}30 0%, transparent 70%)`,
                            filter: 'blur(12px)'
                          }}
                        />
                        <div className="relative h-full rounded-xl bg-[#0c0c14]/80 border border-white/5 group-hover:border-white/20 backdrop-blur-sm p-3 flex flex-col items-center justify-center transition-all duration-300 overflow-hidden">
                          <div className="w-16 h-16 flex items-center justify-center mb-2 transition-transform group-hover:scale-110">
                            {provider.logo ? (
                              <img src={provider.logo} alt={provider.fullName} className="w-full h-full object-contain" />
                            ) : (
                              <span className="text-sm font-bold text-white">{provider.name}</span>
                            )}
                          </div>
                          <span className="text-[10px] text-gray-500 group-hover:text-gray-400 transition-colors text-center">{provider.fullName}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Tablet: 3 columns */}
                  <div className="hidden sm:grid lg:hidden grid-cols-4 gap-3">
                    {gameProviders.map((provider) => (
                      <div key={`tab-${provider.name}`} className="group relative min-h-[90px]">
                        <div className="h-full rounded-xl bg-[#0c0c14]/80 border border-white/5 group-hover:border-white/15 p-3 flex flex-col items-center justify-center transition-all">
                          <div className="w-14 h-14 flex items-center justify-center mb-1.5">
                            {provider.logo ? (
                              <img src={provider.logo} alt={provider.fullName} className="w-full h-full object-contain" />
                            ) : (
                              <span className="text-sm font-bold text-white">{provider.name}</span>
                            )}
                          </div>
                          <span className="text-[9px] text-gray-500 text-center">{provider.fullName}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Mobile: Horizontal carousel */}
                  <div className="flex sm:hidden gap-2.5 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory">
                    {gameProviders.map((provider) => (
                      <div key={`mob-${provider.name}`} className="flex-shrink-0 w-[95px] snap-start group relative">
                        <div className="h-[90px] rounded-xl bg-[#0c0c14]/80 border border-white/5 p-2.5 flex flex-col items-center justify-center transition-all">
                          <div className="w-12 h-12 flex items-center justify-center mb-1">
                            {provider.logo ? (
                              <img src={provider.logo} alt={provider.fullName} className="w-full h-full object-contain" />
                            ) : (
                              <span className="text-xs font-bold text-white">{provider.name}</span>
                            )}
                          </div>
                          <span className="text-[8px] text-gray-500 text-center leading-tight">{provider.fullName}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Catalog size (computed from the real catalog) */}
              <div className="w-full max-w-4xl mx-auto" data-testid="catalog-size">
                <div className="rounded-xl bg-[#0c0c14]/80 border border-white/5 backdrop-blur-sm p-4 sm:p-5 flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white">{CATALOG_GAMES}</h3>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">Jogos no catálogo</p>
                  </div>
                  <div className="text-left sm:text-right">
                    <h3 className="text-2xl sm:text-3xl font-bold text-white">{CATALOG_PROVIDERS}</h3>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">Provedores</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ════════════════════════════════════════════════════════════════════════
                CTA SECTION - DUAS PLATAFORMAS
            ════════════════════════════════════════════════════════════════════════ */}
            <div className={`mt-8 sm:mt-12 transition-all duration-700 delay-200 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              
              {/* Título da seção com estilo Mario */}
              <div className="flex items-center justify-center gap-3 mb-8">
                <img
                  src="/assets/mario_game/bg-coin.png"
                  alt=""
                  className="w-6 h-6 animate-coin-spin-title"
                  style={{
                    imageRendering: 'pixelated',
                    filter: 'drop-shadow(0 0 6px rgba(251,191,36,0.9))',
                  }}
                />
                <h2
                  className="text-center text-sm sm:text-base md:text-lg font-black tracking-wider"
                  style={{
                    fontFamily: '"Press Start 2P", monospace',
                    color: '#fff',
                    textShadow: '2px 2px 0 #ef4444, 4px 4px 0 #7c2d12, 0 0 16px rgba(239,68,68,0.5)',
                    letterSpacing: '1px',
                  }}
                  data-testid="platform-section-title"
                >
                  ESCOLHA SUA PLATAFORMA
                </h2>
                <img
                  src="/assets/mario_game/bg-coin.png"
                  alt=""
                  className="w-6 h-6 animate-coin-spin-title"
                  style={{
                    imageRendering: 'pixelated',
                    filter: 'drop-shadow(0 0 6px rgba(251,191,36,0.9))',
                    animationDelay: '0.5s',
                  }}
                />
              </div>
              
              {/* Grid com 4 plataformas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto px-4">
                
                {/* SG JOGO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(34, 197, 94, 0.22) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  
                  <button
                    onClick={() => onPlatformSelect('SGJOGO')}
                    data-testid="platform-select-sg-btn"
                    className="w-full block group relative"
                  >
                    <PopularBadge testId="sg-populares-badge" gradient="linear-gradient(180deg, #22c55e 0%, #16a34a 55%, #14532d 100%)" glow="rgba(34,197,94,0.85)" />
                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #22c55e, #16a34a, #22c55e)',
                      backgroundSize: '200% 100%'
                    }} />
                    
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-green-500/30 group-hover:border-green-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-green-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-black flex items-center justify-center p-2">
                            <img src="/assets/platforms/sgjogo.png" alt="SG JOGO" className="w-full h-full object-contain" />
                          </div>
                        </div>
                        
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">SG</span>
                          <span className="text-green-400"> JOGO</span>
                        </h3>
                        
                        <MarioBlockEntrar text="ENTRAR" testId="sg-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>
                
                {/* XG JOGO - LANÇAMENTO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(56, 189, 248, 0.28) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  
                  <button
                    onClick={() => onPlatformSelect('XGJOGO')}
                    data-testid="platform-select-xg-btn"
                    className="w-full block group relative"
                  >
                    <PopularBadge testId="xg-populares-badge" gradient="linear-gradient(180deg, #38bdf8 0%, #0ea5e9 55%, #0369a1 100%)" glow="rgba(56,189,248,0.85)" />

                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #38bdf8, #0ea5e9, #38bdf8)',
                      backgroundSize: '200% 100%'
                    }} />
                    
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-sky-500/30 group-hover:border-sky-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-sky-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-black flex items-center justify-center p-2">
                            <img src="/assets/platforms/xgjogo.png" alt="XG JOGO" className="w-full h-full object-contain" />
                          </div>
                        </div>
                        
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">XG</span>
                          <span className="text-sky-400"> JOGO</span>
                        </h3>
                        
                        <MarioBlockEntrar text="ENTRAR" testId="xg-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>
                
                {/* VG JOGO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.22) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  
                  <button
                    onClick={() => onPlatformSelect('VGJOGO')}
                    data-testid="platform-select-vg-btn"
                    className="w-full block group relative"
                  >
                    <PopularBadge testId="vg-populares-badge" gradient="linear-gradient(180deg, #a855f7 0%, #8b5cf6 55%, #5b21b6 100%)" glow="rgba(168,85,247,0.85)" />
                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #8b5cf6, #a855f7, #8b5cf6)',
                      backgroundSize: '200% 100%'
                    }} />
                    
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-violet-500/30 group-hover:border-violet-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-violet-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-black flex items-center justify-center">
                            <img src="/assets/platforms/vgjogo_select.png" alt="VG JOGO" className="w-full h-full object-cover" />
                          </div>
                        </div>
                        
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">VG</span>
                          <span className="text-violet-400"> JOGO</span>
                        </h3>
                        
                        <MarioBlockEntrar text="ENTRAR" testId="vg-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>
                
                {/* HG JOGO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(16, 185, 129, 0.2) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  
                  <button
                    onClick={() => onPlatformSelect('HGJOGO')}
                    data-testid="platform-select-btn"
                    className="w-full block group relative"
                  >
                    <PopularBadge testId="hg-populares-badge" gradient="linear-gradient(180deg, #10b981 0%, #06b6d4 55%, #0e7490 100%)" glow="rgba(16,185,129,0.85)" />
                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #10b981, #06b6d4, #10b981)',
                      backgroundSize: '200% 100%'
                    }} />
                    
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-emerald-500/30 group-hover:border-emerald-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-emerald-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/15 border border-white/15 overflow-hidden">
                            <img src="/assets/platforms/hgjogo_select.png" alt="HG JOGO" className="w-full h-full object-cover" />
                          </div>
                        </div>
                        
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">HG</span>
                          <span className="text-cyan-400"> JOGO</span>
                        </h3>
                        
                        <MarioBlockEntrar text="ENTRAR" testId="hg-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>
                
                {/* EG JOGO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(249, 115, 22, 0.22) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  
                  <button
                    onClick={() => onPlatformSelect('EGJOGO')}
                    data-testid="platform-select-eg-btn"
                    className="w-full block group relative"
                  >
                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #f97316, #fb923c, #f97316)',
                      backgroundSize: '200% 100%'
                    }} />
                    
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-orange-500/30 group-hover:border-orange-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-orange-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-black flex items-center justify-center p-2">
                            <img src="/assets/platforms/egjogo.png" alt="EG JOGO" className="w-full h-full object-contain" />
                          </div>
                        </div>
                        
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">EG</span>
                          <span className="text-orange-400"> JOGO</span>
                        </h3>
                        
                        <MarioBlockEntrar text="ENTRAR" testId="eg-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>
                
                {/* FG JOGO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(168, 85, 247, 0.2) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  
                  <button
                    onClick={() => onPlatformSelect('FGJOGO')}
                    data-testid="platform-select-fg-btn"
                    className="w-full block group relative"
                  >
                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #a855f7, #ec4899, #a855f7)',
                      backgroundSize: '200% 100%'
                    }} />
                    
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-purple-500/30 group-hover:border-purple-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-purple-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden">
                            <img src="/assets/platforms/fgjogo_cta.jpg" alt="FG JOGO" className="w-full h-full object-cover" />
                          </div>
                        </div>
                        
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">FG</span>
                          <span className="text-pink-500"> JOGO</span>
                        </h3>
                        
                        <MarioBlockEntrar text="ENTRAR" testId="fg-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>

                {/* MG JOGO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(20, 184, 166, 0.22) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  <button
                    onClick={() => onPlatformSelect('MGJOGO')}
                    data-testid="platform-select-mg-btn"
                    className="w-full block group relative"
                  >
                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #14b8a6, #2dd4bf, #14b8a6)',
                      backgroundSize: '200% 100%'
                    }} />
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-teal-500/30 group-hover:border-teal-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-teal-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-black flex items-center justify-center p-2">
                            <img src="/assets/platforms/mgjogo.png" alt="MG JOGO" className="w-full h-full object-contain" />
                          </div>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">MG</span>
                          <span className="text-teal-400"> JOGO</span>
                        </h3>
                        <MarioBlockEntrar text="ENTRAR" testId="mg-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>

                {/* WG JOGO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.22) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  <button
                    onClick={() => onPlatformSelect('WGJOGO')}
                    data-testid="platform-select-wg-btn"
                    className="w-full block group relative"
                  >
                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #3b82f6, #60a5fa, #3b82f6)',
                      backgroundSize: '200% 100%'
                    }} />
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-blue-500/30 group-hover:border-blue-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-blue-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-black flex items-center justify-center p-2">
                            <img src="/assets/platforms/wgjogo.png" alt="WG JOGO" className="w-full h-full object-contain" />
                          </div>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">WG</span>
                          <span className="text-blue-400"> JOGO</span>
                        </h3>
                        <MarioBlockEntrar text="ENTRAR" testId="wg-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>

                {/* AG JOGO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(239, 68, 68, 0.22) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  <button
                    onClick={() => onPlatformSelect('AGJOGO')}
                    data-testid="platform-select-ag-btn"
                    className="w-full block group relative"
                  >
                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #ef4444, #f87171, #ef4444)',
                      backgroundSize: '200% 100%'
                    }} />
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-red-500/30 group-hover:border-red-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-red-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-black flex items-center justify-center p-2">
                            <img src="/assets/platforms/agjogo.png" alt="AG JOGO" className="w-full h-full object-contain" />
                          </div>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">AG</span>
                          <span className="text-red-400"> JOGO</span>
                        </h3>
                        <MarioBlockEntrar text="ENTRAR" testId="ag-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>

                {/* BG JOGO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(99, 102, 241, 0.22) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  <button
                    onClick={() => onPlatformSelect('BGJOGO')}
                    data-testid="platform-select-bg-btn"
                    className="w-full block group relative"
                  >
                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #6366f1, #818cf8, #6366f1)',
                      backgroundSize: '200% 100%'
                    }} />
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-indigo-500/30 group-hover:border-indigo-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-indigo-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-black flex items-center justify-center p-2">
                            <img src="/assets/platforms/bgjogo.png" alt="BG JOGO" className="w-full h-full object-contain" />
                          </div>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">BG</span>
                          <span className="text-indigo-400"> JOGO</span>
                        </h3>
                        <MarioBlockEntrar text="ENTRAR" testId="bg-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>

                {/* YG JOGO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(234, 179, 8, 0.22) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  <button
                    onClick={() => onPlatformSelect('YGJOGO')}
                    data-testid="platform-select-yg-btn"
                    className="w-full block group relative"
                  >
                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #eab308, #facc15, #eab308)',
                      backgroundSize: '200% 100%'
                    }} />
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-yellow-500/30 group-hover:border-yellow-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-yellow-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-black flex items-center justify-center p-2">
                            <img src="/assets/platforms/ygjogo.png" alt="YG JOGO" className="w-full h-full object-contain" />
                          </div>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">YG</span>
                          <span className="text-yellow-400"> JOGO</span>
                        </h3>
                        <MarioBlockEntrar text="ENTRAR" testId="yg-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>

                {/* DG JOGO */}
                <div className="relative">
                  <div className="absolute -inset-4 rounded-3xl animate-breathing-glow pointer-events-none" style={{
                    background: 'radial-gradient(ellipse at center, rgba(168, 85, 247, 0.22) 0%, transparent 70%)',
                    filter: 'blur(20px)'
                  }} />
                  <button
                    onClick={() => onPlatformSelect('DGJOGO')}
                    data-testid="platform-select-dg-btn"
                    className="w-full block group relative"
                  >
                    <div className="absolute -inset-[2px] rounded-2xl animate-cta-breathing-border" style={{
                      background: 'linear-gradient(90deg, #a855f7, #c084fc, #a855f7)',
                      backgroundSize: '200% 100%'
                    }} />
                    <div className="relative rounded-2xl bg-gradient-to-br from-[#12121a] to-[#0a0a12] p-5 border border-purple-500/30 group-hover:border-purple-400/50 transition-all duration-300 group-hover:translate-y-[-4px] group-hover:shadow-xl group-hover:shadow-purple-500/20 overflow-hidden">
                      <div className="relative flex flex-col items-center text-center">
                        <div className="relative mb-3">
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-black flex items-center justify-center p-2">
                            <img src="/assets/platforms/dgjogo.png" alt="DG JOGO" className="w-full h-full object-contain" />
                          </div>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold mb-1">
                          <span className="text-white">DG</span>
                          <span className="text-purple-400"> JOGO</span>
                        </h3>
                        <MarioBlockEntrar text="ENTRAR" testId="dg-entrar-block" />
                      </div>
                    </div>
                  </button>
                </div>
              </div>
              
              {/* Info abaixo */}
              <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-6">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span>Ambiente seguro • Alta performance • Online 24h</span>
              </div>
            </div>
          </div>
        </main>

        {/* ════════════════════════════════════════════════════════════════════════
            FOOTER
        ════════════════════════════════════════════════════════════════════════ */}
        <footer ref={footerRef} className={`mt-12 sm:mt-16 bg-[#08080e] border-t border-white/5 transition-all duration-700 ${footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          
          {/* LINE 1: Responsibility Badges — Mario block style */}
          <div className="py-5 sm:py-6 border-b border-white/5 bg-[#0a0a12]">
            <div className="max-w-7xl mx-auto px-2 sm:px-6">
              <div className="flex items-center justify-between sm:justify-center gap-2 sm:gap-10">
                {responsibilityBadges.map((b, idx) => (
                  <div 
                    key={b.name} 
                    className={`flex items-center gap-1.5 sm:gap-3 text-gray-300 transition-all duration-500 hover:text-white ${footerVisible ? 'opacity-100' : 'opacity-0'}`}
                    style={{ transitionDelay: `${idx * 100}ms` }}
                  >
                    <div
                      className="w-7 h-7 sm:w-10 sm:h-10 flex items-center justify-center text-[8px] sm:text-xs font-black flex-shrink-0 transition-transform hover:-translate-y-0.5"
                      style={{
                        background: 'linear-gradient(180deg, #fef08a 0%, #fde047 30%, #fbbf24 65%, #d97706 100%)',
                        border: '2px solid #000',
                        borderRadius: '4px',
                        boxShadow: '2px 2px 0 #000, inset 0 1px 0 rgba(255,255,255,0.5), inset 0 -2px 0 rgba(120,53,15,0.4)',
                        color: '#000',
                        fontFamily: '"Press Start 2P", monospace',
                        textShadow: '1px 1px 0 rgba(255,255,255,0.4)',
                        imageRendering: 'pixelated',
                      }}
                    >
                      {b.icon}
                    </div>
                    <span
                      className="text-[8px] sm:text-[9px] font-black tracking-wider whitespace-nowrap uppercase"
                      style={{ fontFamily: '"Press Start 2P", monospace' }}
                    >
                      {b.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* LINE 2: Providers + Text */}
          <div className="py-5 border-b border-white/5 bg-[#09090f]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 mb-3">
                {gameProviders.map((p, idx) => (
                  <div 
                    key={`f-${p.name}`}
                    className={`w-14 h-14 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-105 hover:brightness-125 overflow-hidden ${footerVisible ? 'opacity-100' : 'opacity-0'}`}
                    style={{ 
                      transitionDelay: `${200 + idx * 80}ms`
                    }}
                  >
                    {p.logo ? (
                      <img src={p.logo} alt={p.fullName} className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-xs font-bold text-gray-400">{p.name}</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 mt-1">
                <img
                  src="/assets/mario_game/bg-coin.png"
                  alt=""
                  className="w-3.5 h-3.5 animate-coin-spin-title"
                  style={{
                    imageRendering: 'pixelated',
                    filter: 'drop-shadow(0 0 4px rgba(251,191,36,0.9))',
                  }}
                />
                <p
                  className="text-center text-[9px] text-amber-300/70 tracking-wider"
                  style={{
                    fontFamily: '"Press Start 2P", monospace',
                    textShadow: '1px 1px 0 rgba(0,0,0,0.8)',
                  }}
                >
                  NOVOS FORNECEDORES EM BREVE
                </p>
                <img
                  src="/assets/mario_game/bg-coin.png"
                  alt=""
                  className="w-3.5 h-3.5 animate-coin-spin-title"
                  style={{
                    imageRendering: 'pixelated',
                    filter: 'drop-shadow(0 0 4px rgba(251,191,36,0.9))',
                    animationDelay: '0.5s',
                  }}
                />
              </div>
            </div>
          </div>
          
          {/* LINE 3: Platform Logos Grid */}
          <div className="py-6 bg-[#08080e]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-3 max-w-4xl mx-auto">
                {platformLogos.map((p, idx) => (
                  <div 
                    key={p.name}
                    className={`aspect-square flex items-center justify-center overflow-hidden transition-all duration-300 ${footerVisible ? 'opacity-100' : 'opacity-0'}`}
                    style={{ transitionDelay: `${400 + idx * 50}ms` }}
                  >
                    {p.logo ? (
                      <img 
                        src={p.logo} 
                        alt={p.name} 
                        className="w-full h-full object-contain hover:scale-105 transition-all" 
                      />
                    ) : (
                      <span className="text-[8px] sm:text-[9px] font-bold text-gray-600">{p.name}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Copyright */}
          <div className="py-3 px-4 sm:px-6 border-t border-white/5">
            <div className="max-w-7xl mx-auto flex items-center justify-between text-xs text-gray-600">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🍄</span>
                <span className="font-bold text-red-500">MARIO</span>
                <span className="ml-1">© 2026</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="hidden sm:inline">Tudo funcionando</span>
                <span className="sm:hidden">Online</span>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          PREMIUM ANIMATIONS
      ════════════════════════════════════════════════════════════════════════ */}
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }

        /* ═══════ LIVE ACTIVITY TICKER ANIMATIONS ═══════ */
        @keyframes ticker-slide {
          0% { transform: translateX(10px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        .animate-ticker-slide { animation: ticker-slide 0.5s ease-out; }
        
        @keyframes ticker-glow {
          0%, 100% { text-shadow: 0 0 5px rgba(16, 185, 129, 0.5); }
          50% { text-shadow: 0 0 15px rgba(16, 185, 129, 0.8), 0 0 25px rgba(16, 185, 129, 0.4); }
        }
        .animate-ticker-glow { animation: ticker-glow 2s ease-in-out infinite; }
        
        @keyframes live-heartbeat {
          0%, 100% { transform: scale(1); }
          15% { transform: scale(1.3); }
          30% { transform: scale(1); }
          45% { transform: scale(1.2); }
          60% { transform: scale(1); }
        }
        .animate-live-heartbeat { animation: live-heartbeat 1.5s ease-in-out infinite; }
        
        @keyframes live-ping {
          0% { transform: scale(1); opacity: 1; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        .animate-live-ping { animation: live-ping 1.5s ease-out infinite; }

        /* ═══════ HEARTBEAT ACCURACY BAR ANIMATIONS ═══════ */
        @keyframes heartbeat-glow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          15% { opacity: 0.8; transform: scale(1.02); }
          30% { opacity: 0.4; transform: scale(1); }
          45% { opacity: 0.7; transform: scale(1.01); }
          60% { opacity: 0.3; transform: scale(1); }
        }
        .animate-heartbeat-glow { animation: heartbeat-glow 2s ease-in-out infinite; }
        
        @keyframes accuracy-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
        .animate-accuracy-pulse { animation: accuracy-pulse 3s ease-in-out infinite; }
        
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
          15% { transform: scale(1.4); box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.4); }
          30% { transform: scale(1); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
          45% { transform: scale(1.3); box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.3); }
          60% { transform: scale(1); box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }
        .animate-heartbeat { animation: heartbeat 1.5s ease-in-out infinite; }
        
        @keyframes heartbeat-ring {
          0%, 100% { transform: scale(1); opacity: 0; }
          15% { transform: scale(2); opacity: 0.5; }
          30% { transform: scale(3); opacity: 0; }
        }
        .animate-heartbeat-ring { animation: heartbeat-ring 1.5s ease-out infinite; }
        
        @keyframes bar-heartbeat {
          0%, 100% { box-shadow: 0 0 10px rgba(16, 185, 129, 0.2); }
          15% { box-shadow: 0 0 25px rgba(16, 185, 129, 0.5), 0 0 40px rgba(16, 185, 129, 0.3); }
          30% { box-shadow: 0 0 10px rgba(16, 185, 129, 0.2); }
          45% { box-shadow: 0 0 20px rgba(16, 185, 129, 0.4); }
          60% { box-shadow: 0 0 10px rgba(16, 185, 129, 0.2); }
        }
        .animate-bar-heartbeat { animation: bar-heartbeat 2s ease-in-out infinite; }
        
        @keyframes live-indicator {
          0%, 100% { border-color: rgba(16, 185, 129, 0.3); }
          50% { border-color: rgba(16, 185, 129, 0.6); }
        }
        .animate-live-indicator { animation: live-indicator 2s ease-in-out infinite; }
        
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.8; box-shadow: 0 0 10px rgba(255,255,255,0.6); }
          50% { opacity: 1; box-shadow: 0 0 20px rgba(255,255,255,1), 0 0 30px rgba(16, 185, 129, 0.8); }
        }
        .animate-pulse-glow { animation: pulse-glow 1s ease-in-out infinite; }

        /* ═══════ RECENT WINS FEED ANIMATIONS ═══════ */
        @keyframes feed-slide-in {
          0% { transform: translateX(-20px); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
        .animate-feed-slide-in { animation: feed-slide-in 0.4s ease-out; }

        /* ═══════ MAIN CTA FOCAL POINT ANIMATIONS ═══════ */
        @keyframes breathing-glow {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        .animate-breathing-glow { animation: breathing-glow 3s ease-in-out infinite; }

        /* ═══════ RECOMENDADO BADGE (bob + flash) ═══════ */
        @keyframes recommended-bob {
          0%, 100% { transform: translateY(0) rotate(-3deg); }
          50%      { transform: translateY(-3px) rotate(3deg); }
        }
        .animate-recommended-bob {
          animation: recommended-bob 1.2s ease-in-out infinite;
          transform-origin: center;
        }

        /* ═══════ Coin spin decoration on section titles ═══════ */
        @keyframes coin-spin-title {
          0%, 100% { transform: scaleX(1); filter: drop-shadow(0 0 6px rgba(251,191,36,0.9)) brightness(1); }
          25%      { transform: scaleX(0.2); filter: drop-shadow(0 0 10px rgba(253,224,71,1)) brightness(1.3); }
          50%      { transform: scaleX(-1); filter: drop-shadow(0 0 6px rgba(251,191,36,0.9)) brightness(1); }
          75%      { transform: scaleX(-0.2); filter: drop-shadow(0 0 10px rgba(253,224,71,1)) brightness(1.3); }
        }
        .animate-coin-spin-title {
          animation: coin-spin-title 2.2s linear infinite;
        }
        
        @keyframes cta-breathing-border {
          0% { background-position: 0% 50%; opacity: 0.7; }
          50% { background-position: 100% 50%; opacity: 1; }
          100% { background-position: 200% 50%; opacity: 0.7; }
        }
        .animate-cta-breathing-border { animation: cta-breathing-border 4s ease-in-out infinite; }
        
        @keyframes focal-pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }
        .animate-focal-pulse { animation: focal-pulse 2s ease-in-out infinite; }
        
        @keyframes button-breathe {
          0%, 100% { transform: scale(1); box-shadow: 0 10px 30px rgba(239, 68, 68, 0.4); }
          50% { transform: scale(1.02); box-shadow: 0 15px 40px rgba(239, 68, 68, 0.6); }
        }
        .animate-button-breathe { animation: button-breathe 2s ease-in-out infinite; }
        
        @keyframes button-inner-glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        .animate-button-inner-glow { animation: button-inner-glow 2s ease-in-out infinite; }
        
        @keyframes button-shine-fast {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .animate-button-shine-fast { animation: button-shine-fast 2s ease-in-out infinite; }
        
        @keyframes arrow-pulse {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(6px); }
        }
        .animate-arrow-pulse { animation: arrow-pulse 1s ease-in-out infinite; }
        
        @keyframes icon-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-icon-float { animation: icon-float 3s ease-in-out infinite; }
        
        @keyframes icon-glow-pulse {
          0%, 100% { opacity: 0.4; box-shadow: 0 0 10px rgba(239, 68, 68, 0.3); }
          50% { opacity: 0.8; box-shadow: 0 0 25px rgba(239, 68, 68, 0.6); }
        }
        .animate-icon-glow-pulse { animation: icon-glow-pulse 2s ease-in-out infinite; }
        
        @keyframes text-glow {
          0%, 100% { text-shadow: 0 0 5px rgba(251, 191, 36, 0.3); }
          50% { text-shadow: 0 0 15px rgba(251, 191, 36, 0.6), 0 0 25px rgba(251, 191, 36, 0.3); }
        }
        .animate-text-glow { animation: text-glow 2s ease-in-out infinite; }

        /* Background */
        @keyframes bg-breathing {
          0%, 100% { filter: brightness(1); }
          50% { filter: brightness(1.02); }
        }
        .animate-bg-breathing { animation: bg-breathing 8s ease-in-out infinite; }

        @keyframes glow-pulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-glow-pulse { animation: glow-pulse 6s ease-in-out infinite; }
        .animate-glow-pulse-delay { animation: glow-pulse 6s ease-in-out infinite 3s; }

        @keyframes float-particle {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.2; }
          50% { transform: translateY(-25px) translateX(8px); opacity: 0.5; }
        }
        .animate-float-particle { animation: float-particle var(--duration, 12s) ease-in-out infinite; }

        @keyframes star-sparkle {
          0%, 100% { opacity: 0; transform: scale(0.5); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        .animate-star-sparkle { animation: star-sparkle 3s ease-in-out infinite; }

        @keyframes beam-pass {
          0% { transform: translateY(-100%); opacity: 0; }
          50% { opacity: 0.4; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        .animate-beam-pass { animation: beam-pass 12s ease-in-out infinite; }
        .animate-beam-pass-delay { animation: beam-pass 15s ease-in-out infinite 5s; }

        /* Title */
        @keyframes title-entrance {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-title-entrance { 
          animation: title-entrance 0.6s ease-out forwards;
          opacity: 0;
        }

        /* Arcade Core */
        @keyframes ring-ultra-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-ring-ultra-slow { animation: ring-ultra-slow 60s linear infinite; }

        @keyframes ring-slow-reverse {
          from { transform: rotate(360deg); }
          to { transform: rotate(0deg); }
        }
        .animate-ring-slow-reverse { animation: ring-slow-reverse 45s linear infinite; }

        @keyframes ring-pulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.02); opacity: 0.7; }
        }
        .animate-ring-pulse { animation: ring-pulse 4s ease-in-out infinite; }

        @keyframes core-glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
        .animate-core-glow { animation: core-glow 5s ease-in-out infinite; }

        @keyframes nucleus-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 60px rgba(124, 58, 237, 0.4); }
          50% { transform: scale(1.03); box-shadow: 0 0 80px rgba(124, 58, 237, 0.6); }
        }
        .animate-nucleus-pulse { animation: nucleus-pulse 4s ease-in-out infinite; }

        @keyframes scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(300%); }
        }
        .animate-scanline { animation: scanline 5s ease-in-out infinite; }

        /* Orbits - different speeds for parallax */
        @keyframes orbit-fast {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-orbit-fast { animation: orbit-fast 12s linear infinite; }

        @keyframes orbit-medium {
          from { transform: rotate(120deg); }
          to { transform: rotate(480deg); }
        }
        .animate-orbit-medium { animation: orbit-medium 18s linear infinite; }

        @keyframes orbit-slow {
          from { transform: rotate(240deg); }
          to { transform: rotate(600deg); }
        }
        .animate-orbit-slow { animation: orbit-slow 25s linear infinite; }

        @keyframes particle-float {
          0%, 100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(-15px); opacity: 0.8; }
        }
        .animate-particle-float { animation: particle-float 3s ease-in-out infinite; }

        /* Cards */
        @keyframes border-glow {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.9; }
        }
        .animate-border-glow { animation: border-glow 3s ease-in-out infinite; }

        @keyframes pulse-subtle {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        .animate-pulse-subtle { animation: pulse-subtle 2.5s ease-in-out infinite; }

        @keyframes bar-shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .animate-bar-shine { animation: bar-shine 2s linear infinite; }

        /* CTA */
        @keyframes border-neon {
          0% { background-position: 0% 50%; }
          100% { background-position: 300% 50%; }
        }
        .animate-border-neon { animation: border-neon 4s linear infinite; }

        @keyframes cta-shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .animate-cta-shine { animation: cta-shine 4s ease-in-out infinite; }

        @keyframes glass-reflection {
          0% { transform: translateX(-100%) rotate(45deg); }
          100% { transform: translateX(200%) rotate(45deg); }
        }
        .animate-glass-reflection { animation: glass-reflection 5s ease-in-out infinite; }

        @keyframes thumbnail-glow-blue {
          0%, 100% { box-shadow: 0 0 10px rgba(59, 130, 246, 0.3); }
          50% { box-shadow: 0 0 25px rgba(59, 130, 246, 0.5); }
        }
        .animate-thumbnail-glow-blue { animation: thumbnail-glow-blue 3s ease-in-out infinite; }

        @keyframes arrow-nudge {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(4px); }
        }
        .animate-arrow-nudge { animation: arrow-nudge 1.5s ease-in-out infinite; }

        /* ═══════ PREMIUM MARIO HOLOGRAM ANIMATIONS ═══════ */
        
        /* Mario breathing motion - 0.98 → 1.00 → 0.98 (3-4s) */
        @keyframes mario-breathe {
          0%, 100% { transform: scale(0.98); }
          50% { transform: scale(1.0); }
        }
        .animate-mario-breathe { animation: mario-breathe 3.5s ease-in-out infinite; }
        
        /* Mario floating - up/down 8px (5s) */
        @keyframes mario-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-mario-float { animation: mario-float 5s ease-in-out infinite; }

        /* ═══════ MARIO ORBIT HEADBUTT JUMP ═══════ */
        /* Every 4.5s Mario does a big vertical jump to reach the orbiting platforms (top of circle).
           Jump distance scales with breakpoint: 150px mobile / 185px tablet / 220px desktop. */
        @keyframes mario-orbit-jump {
          0%, 55%, 100% { transform: translateY(0) scale(1, 1); }
          60%           { transform: translateY(-10px) scale(1.06, 0.94); }  /* pre-squat */
          75%           { transform: translateY(-150px) scale(0.96, 1.06); } /* peak = impact */
          80%           { transform: translateY(-142px) scale(1, 1); }
          92%           { transform: translateY(-28px) scale(1, 1); }
          98%           { transform: translateY(0) scale(1.1, 0.9); }        /* landing squash */
        }
        .animate-mario-orbit-jump {
          animation: mario-orbit-jump 4.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite;
        }
        @media (min-width: 640px) {
          @keyframes mario-orbit-jump {
            0%, 55%, 100% { transform: translateY(0) scale(1, 1); }
            60%           { transform: translateY(-10px) scale(1.06, 0.94); }
            75%           { transform: translateY(-185px) scale(0.96, 1.06); }
            80%           { transform: translateY(-175px) scale(1, 1); }
            92%           { transform: translateY(-30px) scale(1, 1); }
            98%           { transform: translateY(0) scale(1.1, 0.9); }
          }
        }
        @media (min-width: 1024px) {
          @keyframes mario-orbit-jump {
            0%, 55%, 100% { transform: translateY(0) scale(1, 1); }
            60%           { transform: translateY(-12px) scale(1.06, 0.94); }
            75%           { transform: translateY(-220px) scale(0.96, 1.06); }
            80%           { transform: translateY(-208px) scale(1, 1); }
            92%           { transform: translateY(-35px) scale(1, 1); }
            98%           { transform: translateY(0) scale(1.1, 0.9); }
          }
        }

        /* Impact sparks appear only at peak of jump (~75% of 4.5s = at 3.375s) */
        @keyframes mario-orbit-impact {
          0%, 72%, 82%, 100% { opacity: 0; transform: translateX(-50%) scale(0.5); }
          75%                { opacity: 1; transform: translateX(-50%) scale(1.2); }
          80%                { opacity: 0.6; transform: translateX(-50%) scale(1); }
        }
        .animate-mario-orbit-impact {
          animation: mario-orbit-impact 4.5s ease-out infinite;
        }

        /* Stars flying out on impact */
        @keyframes star-fly-left {
          0%, 72%, 100% { opacity: 0; transform: translate(0, 0) rotate(0deg); }
          75%           { opacity: 1; transform: translate(-14px, -6px) rotate(-20deg); }
          82%           { opacity: 0; transform: translate(-24px, 4px) rotate(-40deg); }
        }
        .animate-star-fly-left { animation: star-fly-left 4.5s ease-out infinite; }

        @keyframes star-fly-up {
          0%, 72%, 100% { opacity: 0; transform: translate(0, 0) scale(0.5); }
          75%           { opacity: 1; transform: translate(0, -14px) scale(1.4); }
          82%           { opacity: 0; transform: translate(0, -22px) scale(0.9); }
        }
        .animate-star-fly-up { animation: star-fly-up 4.5s ease-out infinite; }

        @keyframes star-fly-right {
          0%, 72%, 100% { opacity: 0; transform: translate(0, 0) rotate(0deg); }
          75%           { opacity: 1; transform: translate(14px, -6px) rotate(20deg); }
          82%           { opacity: 0; transform: translate(24px, 4px) rotate(40deg); }
        }
        .animate-star-fly-right { animation: star-fly-right 4.5s ease-out infinite; }

        /* Shockwave ring expanding on impact */
        @keyframes shockwave {
          0%, 72%, 100% { opacity: 0; transform: translateX(-50%) scale(0.3); }
          75%           { opacity: 0.9; transform: translateX(-50%) scale(1); }
          82%           { opacity: 0; transform: translateX(-50%) scale(2.2); }
        }
        .animate-shockwave { animation: shockwave 4.5s ease-out infinite; }
        
        /* Premium scanline - passes every 2.5s with brightness */
        @keyframes scanline-premium {
          0% { transform: translateY(-100%); opacity: 0; }
          10% { opacity: 0.8; }
          50% { opacity: 1; }
          90% { opacity: 0.8; }
          100% { transform: translateY(600%); opacity: 0; }
        }
        .animate-scanline-premium { animation: scanline-premium 2.5s ease-in-out infinite; }
        
        /* Hologram shimmer/noise effect - very subtle */
        @keyframes holo-shimmer {
          0%, 100% { opacity: 0.3; transform: translateY(0); }
          25% { opacity: 0.5; transform: translateY(-1px); }
          50% { opacity: 0.3; transform: translateY(1px); }
          75% { opacity: 0.4; transform: translateY(-0.5px); }
        }
        .animate-holo-shimmer { animation: holo-shimmer 0.4s ease-in-out infinite; }
        .animate-mario-shimmer { animation: holo-shimmer 0.5s ease-in-out infinite; }
        
        /* Glitch spark effect - 1-2 frames */
        @keyframes glitch-spark {
          0% { 
            transform: translate(-2px, 1px) skewX(-2deg);
            filter: brightness(1.8) hue-rotate(20deg);
          }
          50% { 
            transform: translate(2px, -1px) skewX(2deg);
            filter: brightness(2) hue-rotate(-20deg);
          }
          100% { 
            transform: translate(0, 0) skewX(0deg);
            filter: brightness(1);
          }
        }
        .animate-glitch-spark { animation: glitch-spark 0.08s linear; }
        
        /* Radar rings - expand/fade repeatedly */
        @keyframes radar-ring-1 {
          0%, 100% { 
            transform: scale(1) rotate(0deg); 
            opacity: 0.4;
            border-color: rgba(124, 58, 237, 0.3);
          }
          50% { 
            transform: scale(1.08) rotate(180deg); 
            opacity: 0.7;
            border-color: rgba(124, 58, 237, 0.5);
          }
        }
        .animate-radar-ring-1 { animation: radar-ring-1 8s ease-in-out infinite; }
        
        @keyframes radar-ring-2 {
          0%, 100% { 
            transform: scale(1) rotate(0deg); 
            opacity: 0.3;
          }
          50% { 
            transform: scale(1.05) rotate(-120deg); 
            opacity: 0.6;
          }
        }
        .animate-radar-ring-2 { animation: radar-ring-2 6s ease-in-out infinite 1s; }
        
        @keyframes radar-ring-3 {
          0%, 100% { 
            transform: scale(1); 
            opacity: 0.35;
            box-shadow: 0 0 20px rgba(124, 58, 237, 0.18);
          }
          50% { 
            transform: scale(1.03); 
            opacity: 0.55;
            box-shadow: 0 0 35px rgba(124, 58, 237, 0.3);
          }
        }
        .animate-radar-ring-3 { animation: radar-ring-3 4s ease-in-out infinite 0.5s; }
        
        /* Core glow premium */
        @keyframes core-glow-premium {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.02); }
        }
        .animate-core-glow-premium { animation: core-glow-premium 5s ease-in-out infinite; }
        
        /* Nucleus breathing */
        @keyframes nucleus-breathe {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 0 50px rgba(124, 58, 237, 0.35), inset 0 0 30px rgba(59, 130, 246, 0.2);
          }
          50% { 
            transform: scale(1.03);
            box-shadow: 0 0 70px rgba(124, 58, 237, 0.5), inset 0 0 40px rgba(59, 130, 246, 0.3);
          }
        }
        .animate-nucleus-breathe { animation: nucleus-breathe 3.5s ease-in-out infinite; }
        
        /* Shadow breathing sync with Mario */
        @keyframes shadow-breathe {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.15); opacity: 0.7; }
        }
        .animate-shadow-breathe { animation: shadow-breathe 5s ease-in-out infinite; }
        
        /* ═══════ ORBITING ITEMS WITH PARALLAX ═══════ */
        
        /* Coin orbit - fastest (18s) */
        @keyframes orbit-coin-parallax {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-orbit-coin-parallax { animation: orbit-coin-parallax 18s linear infinite; }
        
        /* Coin 3D spin */
        @keyframes coin-3d-spin {
          0% { transform: rotateY(0deg) scale(1); }
          25% { transform: rotateY(90deg) scale(0.9); }
          50% { transform: rotateY(180deg) scale(1); }
          75% { transform: rotateY(270deg) scale(0.9); }
          100% { transform: rotateY(360deg) scale(1); }
        }
        .animate-coin-3d-spin { animation: coin-3d-spin 4s linear infinite; }
        
        /* Star orbit - medium (25s) */
        @keyframes orbit-star-parallax {
          from { transform: rotate(90deg); }
          to { transform: rotate(450deg); }
        }
        .animate-orbit-star-parallax { animation: orbit-star-parallax 25s linear infinite; }
        
        /* Star pulse premium */
        @keyframes star-pulse-premium {
          0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.8; }
          50% { transform: scale(1.15) rotate(10deg); opacity: 1; }
        }
        .animate-star-pulse-premium { animation: star-pulse-premium 3s ease-in-out infinite; }
        
        /* Mushroom orbit - slowest (35s) */
        @keyframes orbit-mushroom-parallax {
          from { transform: rotate(180deg); }
          to { transform: rotate(540deg); }
        }
        .animate-orbit-mushroom-parallax { animation: orbit-mushroom-parallax 35s linear infinite; }
        
        /* Mushroom bob */
        @keyframes mushroom-bob {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-4px) scale(1.05); }
        }
        .animate-mushroom-bob { animation: mushroom-bob 2.5s ease-in-out infinite; }
        
        /* Ambient particle float */
        @keyframes particle-ambient {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
          50% { transform: translateY(-15px) translateX(5px); opacity: 0.7; }
        }
        .animate-particle-ambient { animation: particle-ambient 4s ease-in-out infinite; }
        
        /* Energy flow */
        @keyframes energy-flow {
          0% { stroke-dashoffset: 20; opacity: 0.3; }
          50% { stroke-dashoffset: 0; opacity: 0.6; }
          100% { stroke-dashoffset: -20; opacity: 0.3; }
        }
        .animate-energy-flow { animation: energy-flow 2s linear infinite; }
        
        /* ═══════ LIVE SIGNALS ANIMATIONS ═══════ */
        @keyframes live-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(16, 185, 129, 0); }
        }
        .animate-live-pulse { animation: live-pulse 2s ease-in-out infinite; }
        
        /* ═══════ ENHANCED CTA ANIMATIONS ═══════ */
        @keyframes cta-border-glow {
          0% { background-position: 0% 50%; opacity: 0.7; }
          50% { background-position: 150% 50%; opacity: 1; }
          100% { background-position: 300% 50%; opacity: 0.7; }
        }
        .animate-cta-border-glow { animation: cta-border-glow 4s linear infinite; }
        
        @keyframes cta-shine-loop {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(100%); }
        }
        .animate-cta-shine-loop { animation: cta-shine-loop 3s ease-in-out infinite; }
        
        @keyframes button-shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        .animate-button-shine { animation: button-shine 2.5s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default PlatformSelector;
